import { DateValue } from '@internationalized/date';
import { CoreAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { coreOutboxEventCountsQueryAtom } from '../_graphql/queryCoreOutboxEventCounts';
import { coreOutboxEventsQueryAtom } from '../_graphql/queryCoreOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type CoreAuditTab = 'events' | 'inbox';

export const $coreAuditTab = atom<CoreAuditTab>('events');

export const $coreAggregateTypeFilter = atom<CoreAggregateType | undefined>(
  undefined,
);

export const $coreAggregateIdFilter = atom<string | undefined>(undefined);

export const $corePagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedCoreEventId = atom<string | undefined>(undefined);

export const $coreDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteCoreOutboxEvents = coreOutboxEventsQueryAtom((get) => {
  const aggregateType = get($coreAggregateTypeFilter);
  const aggregateId = get($coreAggregateIdFilter);
  const pagination = get($corePagination);
  const dateRange = get($coreDateRangeFilter);

  return {
    variables: {
      aggregateTypes: aggregateType ? [aggregateType] : undefined,
      aggregateId,
      pagination,
      createdAtRange:
        dateRange?.start && dateRange?.end
          ? {
              start: dateToZonedDateTimeStart(dateRange.start),
              end: dateToZonedDateTimeEnd(dateRange.end),
            }
          : undefined,
    },
  };
});

export const $remoteCoreOutboxEventCounts = coreOutboxEventCountsQueryAtom();

export const useCoreAuditTab = () => {
  const [tab, setTab] = useAtom($coreAuditTab);
  return [tab, setTab] as const;
};

export const useCoreAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($coreAggregateTypeFilter);
  const [, setAggregateId] = useAtom($coreAggregateIdFilter);

  const setFilterAndResetId = (value: CoreAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useCoreAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($coreAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useCorePagination = () => {
  const [pagination, setPagination] = useAtom($corePagination);
  return [pagination, setPagination] as const;
};

export const useSelectedCoreEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedCoreEventId);
  return [selectedId, setSelectedId] as const;
};

export const useCoreDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($coreDateRangeFilter);
  return [filter, setFilter] as const;
};
